import axios from "axios";
import { X } from "lucide-react";
import { useEffect, useState } from "react";

const CouponModal = ({ onClose, editingCoupon, fetchCoupons }) => {
    const API = process.env.REACT_APP_API_URL;

    const [form, setForm] = useState({
        code: "",
        discountType: "FLAT",
        discountValue: "",
        minOrderAmount: "",
        usageLimit: "",
        startsAt: "",
        expiresAt: "",
    });

    const [errors, setErrors] = useState({});

    //  Input styling
    const inputClass = (field) =>
        `w-full mt-1 rounded-lg px-4 py-3 outline-none border transition
    ${errors[field]
            ? "border-red-400 focus:border-red-500"
            : "border-gray-300 focus:border-accentTextColor"
        }`;


    useEffect(() => {
        if (editingCoupon) {
            setForm({
                ...editingCoupon,
                startsAt: editingCoupon.startsAt?.slice(0, 16),
                expiresAt: editingCoupon.expiresAt?.slice(0, 16),
            });
        }
    }, [editingCoupon]);

    //  Validation
    const validate = () => {
        const newErrors = {};

        if (!form.code) newErrors.code = "Coupon code is required";

        if (!form.discountValue) {
            newErrors.discountValue = "Discount value is required";
        } else if (Number(form.discountValue) < 0) {
            newErrors.discountValue = "Discount cannot be negative";
        } else if (
            form.discountType === "PERCENTAGE" &&
            Number(form.discountValue) > 100
        ) {
            newErrors.discountValue = "Percentage cannot exceed 100";
        }

        if (form.startsAt && form.expiresAt) {
            if (new Date(form.expiresAt) <= new Date(form.startsAt)) {
                newErrors.expiresAt = "Expiry must be after start date";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    //  Save
    const handleSave = async () => {
        if (!validate()) return;

        if (editingCoupon) {
            await axios.put(`${API}/coupons/${editingCoupon._id}`, form);
        } else {
            await axios.post(`${API}/coupons`, form);
        }

        fetchCoupons();
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
            <div className="bg-white w-full max-w-xl h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden">

                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b bg-white sticky top-0 z-10">
                    <h2 className="text-lg font-semibold text-gray-900">
                        {editingCoupon ? "Edit Coupon" : "Create Coupon"}
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-black">
                        <X />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

                    {/* Code */}
                    <div>
                        <label className="text-sm font-medium text-gray-700">
                            Coupon Code
                        </label>
                        <input
                            type="text"
                            placeholder="e.g. NEWUSER50"
                            value={form.code}
                            onChange={(e) =>
                                setForm({ ...form, code: e.target.value.toUpperCase() })
                            }
                            className={inputClass("code")}
                        />
                        {errors.code && (
                            <p className="text-xs text-red-500 mt-1">{errors.code}</p>
                        )}
                    </div>

                    {/* Discount */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-sm font-medium text-gray-700">
                                Discount Type
                            </label>
                            <select
                                value={form.discountType}
                                onChange={(e) =>
                                    setForm({ ...form, discountType: e.target.value })
                                }
                                className={inputClass()}
                            >
                                <option value="FLAT">Flat (₹)</option>
                                <option value="PERCENTAGE">Percentage (%)</option>
                            </select>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-gray-700">
                                Discount Value
                            </label>
                            <input
                                type="number"
                                placeholder={
                                    form.discountType === "FLAT"
                                        ? "e.g. 200"
                                        : "e.g. 10 (no %)"
                                }
                                value={form.discountValue}
                                onChange={(e) =>
                                    setForm({ ...form, discountValue: e.target.value })
                                }
                                className={inputClass("discountValue")}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                {form.discountType === "PERCENTAGE"
                                    ? "Enter number only (10 = 10%). Don’t add %."
                                    : "Flat amount in rupees."}
                            </p>
                            {errors.discountValue && (
                                <p className="text-xs text-red-500 mt-1">
                                    {errors.discountValue}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Order + Usage */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-sm font-medium text-gray-700">
                                Min Order Amount
                            </label>
                            <input
                                type="number"
                                placeholder="e.g. 999"
                                value={form.minOrderAmount}
                                onChange={(e) =>
                                    setForm({ ...form, minOrderAmount: e.target.value })
                                }
                                className={inputClass()}
                            />
                        </div>

                        <div>
                            <label className="text-sm font-medium text-gray-700">
                                Usage Limit
                            </label>
                            <input
                                type="number"
                                placeholder="e.g. 100 (optional)"
                                value={form.usageLimit}
                                onChange={(e) =>
                                    setForm({ ...form, usageLimit: e.target.value })
                                }
                                className={inputClass()}
                            />
                        </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-sm font-medium text-gray-700">
                                Start Date
                            </label>
                            <input
                                type="datetime-local"
                                value={form.startsAt}
                                onChange={(e) =>
                                    setForm({ ...form, startsAt: e.target.value })
                                }
                                className={inputClass()}
                            />
                        </div>

                        <div>
                            <label className="text-sm font-medium text-gray-700">
                                Expiry Date
                            </label>
                            <input
                                type="datetime-local"
                                value={form.expiresAt}
                                onChange={(e) =>
                                    setForm({ ...form, expiresAt: e.target.value })
                                }
                                className={inputClass("expiresAt")}
                            />
                            {errors.expiresAt && (
                                <p className="text-xs text-red-500 mt-1">
                                    {errors.expiresAt}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t bg-white sticky bottom-0">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-md border text-gray-600 hover:bg-gray-100"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleSave}
                        className="px-5 py-2 rounded-md bg-primaryBtnColor text-white font-semibold hover:bg-primaryBtnHoverColor"
                    >
                        {editingCoupon ? "Save Changes" : "Create Coupon"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CouponModal;